import { useEffect, useState } from 'react'
import Modal from '@components/shared/Modal'
import ResearchAssistedImport from './ResearchAssistedImport'
import ResearchCandidateRelevance from './ResearchCandidateRelevance'
import { actionLabel } from './acervoHelpers'
import { candidateFeedback, orderedResearchCandidates } from './researchRelevance'
import { officialProviderUrl } from './jurisprudenceProviderHelpers'
import { REVIEW_CHECKS, RESEARCH_STATES, TRANSPORT_LABELS, reviewableValidation, reviewPayload, validateResearchFile, researchWarning, researchOriginalFileName } from './legalResearchHelpers'
import './legalResearch.css'

function ValidationReview({ validation, issue, document, examined, research, disabled }) {
  const [checks, setChecks] = useState({})
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [confirmPublic, setConfirmPublic] = useState(false)
  const [publicStored, setPublicStored] = useState(false)
  const review = research.job.result.reviews?.find((value) => value.validationId === validation.validationId && value.validationHash === validation.validationHash)
  const notPertinent = candidateFeedback(research.job.result, validation.candidateId, validation.issueId)?.decision === 'NotPertinent'
  const ready = !notPertinent && reviewableValidation(validation, document) && examined?.documentVersionId === document?.documentVersionId
    && examined?.textHash === document?.textHash
  useEffect(() => {
    if (notPertinent) { setChecks({}); setNotes(''); setConfirmPublic(false) }
  }, [notPertinent])
  const complete = REVIEW_CHECKS.every(([key]) => checks[key]) && notes.trim().length >= 10 && notes.length <= 2000
  const submit = async (event) => {
    event.preventDefault()
    if (!ready || !complete || disabled) return
    try { setError(''); await research.review(validation, reviewPayload(validation, checks, notes)) }
    catch (failure) { setError(failure.message) }
  }
  const share = async (event) => {
    event.preventDefault()
    if (disabled || !confirmPublic || notPertinent || !research.canReuse(validation)) return
    try {
      setError('')
      const stored = await research.publicReuse(validation, confirmPublic)
      if (stored) { setPublicStored(true); setConfirmPublic(false) }
    } catch (failure) { setError(failure.message) }
  }
  return <section className="legal-research-validation">
    <h5>{issue?.question || 'Questão identificada na pesquisa'}</h5>
    {validation.analysis?.proposal?.reason && <p>Análise para conferência: {validation.analysis.proposal.reason}</p>}
    <details>
      <summary>Conferências e limites da validação</summary>
      <ul>{validation.rules?.map((rule, index) => <li key={`${rule.code}-${index}`}>
        <strong>{({ Pass: 'Conferido', Fail: 'Bloqueio', Unknown: 'Não confirmado', NotApplicable: 'Não se aplica' })[rule.state] || 'Não confirmado'}:</strong> {rule.reason}
      </li>)}</ul>
      {validation.warnings?.map((warning, index) => <p key={index} className="acervo-muted">{researchWarning(warning)}</p>)}
    </details>
    <div className="legal-research-quotes">{validation.citations?.map((citation) => <blockquote key={citation.citationId}>
      {citation.verifiedCitation ? <>
        <p>{citation.verifiedCitation.quote}</p>
        <small>{citation.verifiedCitation.page ? `Página ${citation.verifiedCitation.page}` : 'Trecho do texto integral'} · {citation.reason}</small>
      </> : <p>{citation.reason}</p>}
    </blockquote>)}</div>
    {review && <p className="acervo-notice" role="status">Revisão registrada para esta questão e versão documental. A API reconfere a elegibilidade antes da geração.</p>}
    {!ready && <p className="acervo-muted">{notPertinent
      ? 'Esta referência foi marcada como não pertinente a esta questão. Uma nova avaliação de pertinência e conferência são necessárias antes de autorizar seu uso.'
      : 'Abra e confira o inteiro teor. A revisão exige documento íntegro, trechos verificáveis e ausência de bloqueios na validação.'}</p>}
    {ready && <form onSubmit={submit} className="legal-research-review">
      <fieldset disabled={disabled}>
        <legend>Conferência humana para uso nesta minuta</legend>
        {REVIEW_CHECKS.map(([key, label]) => <label key={key}>
          <input type="checkbox" checked={Boolean(checks[key])} onChange={(event) => setChecks((previous) => ({ ...previous, [key]: event.target.checked }))} /> {label}
        </label>)}
        <label className="acervo-field">Fundamentação da revisão
          <textarea value={notes} rows={3} onChange={(event) => setNotes(event.target.value)} aria-invalid={notes.length > 2000} />
          <small>{notes.length} / 2.000 caracteres; mínimo de 10.</small>
        </label>
        <button className="acervo-button" type="submit" disabled={!complete}>{review ? 'Registrar nova conferência desta referência' : 'Autorizar referência para esta minuta'}</button>
      </fieldset>
      {error && <p className="acervo-error" role="alert">{error}</p>}
    </form>}
    {publicStored && <p className="acervo-notice" role="status">Documento incluído na base compartilhada de jurisprudência. A pertinência, a fonte e a autorização para citar serão conferidas novamente em cada caso.</p>}
    {!notPertinent && research.canReuse(validation) && <form className="legal-research-public-reuse" onSubmit={share}>
      <fieldset disabled={disabled}>
        <legend>Guardar julgado para outras pesquisas</legend>
        <p className="acervo-muted">A base compartilhada recebe o documento oficial e seus metadados. A pesquisa, as instruções do cliente e a autorização desta minuta ficam separadas.</p>
        <label><input type="checkbox" checked={confirmPublic} onChange={(event) => setConfirmPublic(event.target.checked)} /> Confirmo que este é um documento oficial público revisado e autorizo seu reuso na base compartilhada.</label>
        <button type="submit" className="acervo-button" disabled={!confirmPublic}>Incluir documento na base compartilhada</button>
      </fieldset>
      {error && <p className="acervo-error" role="alert">{error}</p>}
    </form>}
  </section>
}

function CandidateCard({ candidate, document, validations, issues, result, research, disabled, onView }) {
  const [examined, setExamined] = useState(null)
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  const links = (candidate.officialLinks || []).map(officialProviderUrl).filter(Boolean)
  const upload = async () => {
    const failure = validateResearchFile(file)
    if (failure) { setError(failure); return }
    setError('')
    try {
      await research.attach(candidate.candidateId, file)
    } catch { setError('Não foi possível ler o arquivo selecionado. Selecione novamente o inteiro teor.') }
  }
  const view = async () => {
    const text = await onView(document)
    if (text?.documentVersionId === document.documentVersionId && text.textHash === document.textHash) setExamined(text)
  }
  const download = async () => {
    const blob = await research.original(document.versionId)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const anchor = window.document.createElement('a')
    anchor.href = url
    anchor.download = researchOriginalFileName(document.versionId, blob.type)
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return <article className="legal-research-candidate">
    <h4>{candidate.title || `${candidate.court} · ${candidate.nativeId}`}</h4>
    <p className="acervo-muted">{candidate.court}{candidate.processNumber ? ` · Processo ${candidate.processNumber}` : ''}
      {candidate.decisionDate ? ` · Julgamento ${candidate.decisionDate}` : ''}</p>
    {candidate.originalSummary && <details><summary>Ementa / resumo da fonte</summary><p>{candidate.originalSummary}</p></details>}
    {links.length > 0 && <div className="acervo-toolbar-actions">{links.map((url, index) => <a key={url} href={url} target="_blank" rel="noopener noreferrer">Consultar fonte oficial{links.length > 1 ? ` ${index + 1}` : ''}</a>)}</div>}
    {document && <div className="acervo-toolbar-actions">
      <button type="button" className="acervo-button" disabled={disabled} onClick={view}>Conferir inteiro teor</button>
      <button type="button" className="acervo-button" disabled={disabled} onClick={download}>Baixar original</button>
    </div>}
    <ResearchCandidateRelevance candidate={candidate} document={document} result={result} research={research} disabled={disabled} />
    <details>
      <summary>{document ? 'Substituir documento para nova validação' : 'Associar inteiro teor para conferência'}</summary>
      <p className="acervo-muted">Envie o original em PDF, DOCX ou TXT, até 2 MB. A associação exige conferência da identidade e da fonte; a validação será refeita.</p>
      <label className="acervo-field">Documento original
        <input type="file" accept=".pdf,.docx,.txt" disabled={disabled} onChange={(event) => { setFile(event.target.files?.[0] || null); setError('') }} />
      </label>
      <button type="button" className="acervo-button" disabled={disabled || !file} onClick={upload}>Enviar e revalidar</button>
      {error && <p className="acervo-error" role="alert">{error}</p>}
    </details>
    {validations.map((validation) => <ValidationReview key={validation.validationId} validation={validation} document={document}
      issue={issues.find((issue) => issue.issueId === validation.issueId)} examined={examined} research={research} disabled={disabled} />)}
    {validations.length === 0 && <p className="acervo-muted">Este candidato ainda não possui validação documental para uso na minuta.</p>}
  </article>
}

export default function LegalResearchPanel({ research, valid, disabled, useResearch, onUseResearch }) {
  const [visibleDocument, setVisibleDocument] = useState(null)
  const [officialUrl, setOfficialUrl] = useState('')
  const [issueIds, setIssueIds] = useState([])
  const job = research.job
  const result = job?.result
  const issues = result?.extraction?.issues || []
  const locked = disabled || Boolean(research.phase) || research.pending || research.stale || !['Completed', 'Partial'].includes(job?.state)
  useEffect(() => { setVisibleDocument(null); setIssueIds([]); setOfficialUrl('') }, [job?.researchId, research.stale])
  const view = async (document) => {
    const data = await research.text(document.versionId)
    if (data) setVisibleDocument(data)
    return data
  }
  const readSource = (event) => {
    event.preventDefault()
    if (!locked && issueIds.length > 0 && officialUrl.trim()) research.readSource(officialUrl.trim(), issueIds)
  }
  return <section className="acervo-panel legal-research-panel" aria-labelledby="legal-research-title" aria-busy={Boolean(research.phase) || research.pending}>
    <div className="acervo-toolbar">
      <div><h3 id="legal-research-title">Pesquisa de jurisprudência</h3>
        <p className="acervo-muted">Pesquise com os dados do formulário. Confira a cobertura, o documento e a pertinência ao caso antes de autorizar referências.</p></div>
      <div className="acervo-toolbar-actions">
        <button type="button" className="acervo-button" disabled={disabled || !valid || Boolean(research.phase) || research.pending} onClick={research.start}>
          {job && !research.stale ? 'Consultar pesquisa deste contexto' : 'Pesquisar jurisprudência'}
        </button>
        {job && <button type="button" className="acervo-button" disabled={disabled || Boolean(research.phase)} onClick={research.refresh}>Atualizar pesquisa</button>}
        {research.pending && <button type="button" className="acervo-button" disabled={disabled || Boolean(research.phase)} onClick={research.cancel}>Cancelar pesquisa</button>}
        {['Failed', 'Cancelled'].includes(job?.state) && <button type="button" className="acervo-button" disabled={disabled || !research.canRetry || Boolean(research.phase)} onClick={research.retry}>Retomar pesquisa</button>}
      </div>
    </div>
    {research.phase && <p role="status" className="acervo-working">{research.phase}</p>}
    {research.error && <p role="alert" className="acervo-notice acervo-error">{research.error}</p>}
    {job && <p role="status">{RESEARCH_STATES[job.state] || 'Estado da pesquisa indisponível'}{job.attempt > 0 ? ` · Tentativa ${job.attempt}` : ''}</p>}
    {research.pending && <p className="acervo-muted">O processamento continua no servidor. Os resultados serão atualizados automaticamente; a execução depende do worker de pesquisa habilitado.</p>}
    {research.stale && <p role="alert" className="acervo-notice">Os dados do caso foram alterados. Estes resultados pertencem ao contexto anterior e não podem autorizar referências para a nova minuta.</p>}
    {job?.state === 'Failed' && <p className="acervo-error">A pesquisa não foi concluída. Confira as integrações e tente novamente. Nenhuma ausência de jurisprudência foi inferida.</p>}
    {result?.extraction?.mode === 'Shadow' && <p className="acervo-notice">A pesquisa está em modo de observação; suas referências não podem influenciar a minuta.</p>}
    {result?.extraction?.reason && <p className="acervo-notice">{result.extraction.reason}</p>}
    {result?.plan?.gaps?.map((gap, index) => <p key={index} className="acervo-notice">{gap.message}</p>)}
    {result?.warnings?.length > 0 && <details><summary>Alertas da pesquisa ({result.warnings.length})</summary>
      <ul>{result.warnings.map((warning, index) => <li key={index}>{researchWarning(warning)}</li>)}</ul>
    </details>}
    {issues.length > 0 && <details open><summary>Ação e questões pesquisadas</summary>
      {result.extraction.selectedActions?.length > 0 && <p>Ação selecionada: {result.extraction.selectedActions.map((action) => actionLabel(action.actionCode)).join(' · ')}</p>}
      <ul>{issues.map((issue) => <li key={issue.issueId}>{issue.question}
        {issue.legalConcepts?.length > 0 && <p className="acervo-muted">Conceitos pesquisados: {issue.legalConcepts.join(' · ')}</p>}
      </li>)}</ul>
    </details>}
    {result?.search && <>
      <details open><summary>Cobertura das fontes</summary>
        <ul>{result.search.providers?.map((provider, index) => <li key={`${provider.providerId}-${provider.queryId}-${index}`}>
          <strong>{provider.providerId}:</strong> {provider.capability === 'ManualOnly' ? 'Busca manual — consulte a fonte e associe o documento.' : TRANSPORT_LABELS[provider.transport] || 'Consulta não confirmada'}
          {provider.partialCoverage ? ' · Cobertura parcial' : ''}
          {provider.message ? <p className="acervo-muted">{provider.message}</p> : null}
        </li>)}</ul>
        <p className="acervo-muted">Fontes manuais, inacessíveis ou sem resultados não comprovam inexistência de precedentes. A ementa não substitui o inteiro teor.</p>
      </details>
      {result.search.candidates?.length === 0 && <p className="acervo-empty">Nenhum candidato foi retornado nesta execução. Confira a cobertura e as fontes manuais.</p>}
      <p className="acervo-muted">Os candidatos com aderência identificada aparecem pela prioridade de leitura para suas questões. Suas avaliações orientam próximas pesquisas em contextos semelhantes; cada caso continua exigindo conferência jurídica.</p>
      <div className="legal-research-candidates">{orderedResearchCandidates(result).map((candidate) => {
        const document = result.documents?.find((value) => value.candidateId === candidate.candidateId)
        return <CandidateCard key={`${job.researchId}-${candidate.candidateId}-${document?.versionId || ''}`} candidate={candidate} document={document}
          validations={(result.validations || []).filter((value) => value.candidateId === candidate.candidateId)} issues={issues}
          result={result} research={research} disabled={locked} onView={view} />
      })}</div>
      <form className="legal-research-official-link" onSubmit={readSource}>
        <h4>Ler documento por endereço oficial</h4>
        <p className="acervo-muted">Use um endereço atendido pelos leitores oficiais disponíveis, como o PDF do TJSP. Fontes que exigem acesso manual podem ser conferidas externamente e associadas pelo original.</p>
        <fieldset disabled={locked}>
          <legend>Documento e questões às quais será associado</legend>
          <label className="acervo-field">Endereço do documento
            <input type="url" maxLength={2048} value={officialUrl} onChange={(event) => setOfficialUrl(event.target.value)} required />
          </label>
          {issues.map((issue) => <label key={issue.issueId} className="legal-research-issue-choice">
            <input type="checkbox" checked={issueIds.includes(issue.issueId)} onChange={(event) => setIssueIds((previous) => event.target.checked
              ? [...previous, issue.issueId] : previous.filter((id) => id !== issue.issueId))} /> {issue.question}
          </label>)}
          <button type="submit" className="acervo-button" disabled={!officialUrl.trim() || issueIds.length === 0 || issueIds.length > 8}>Ler e revalidar documento</button>
        </fieldset>
      </form>
      <ResearchAssistedImport key={job.researchId} research={research} issues={issues} disabled={locked} />
    </>}
    <label className="legal-research-use">
      <input type="checkbox" checked={useResearch} disabled={disabled || Boolean(research.phase) || (!research.canUse && !useResearch)}
        onChange={(event) => onUseResearch(event.target.checked)} /> Usar referências autorizadas desta pesquisa na próxima geração
    </label>
    <p className="acervo-muted">A API reconfere a revisão, o contexto e a fonte na geração. Alterações ou expiração podem retirar a referência; os alertas aparecem separados da minuta.</p>
    <Modal isOpen={Boolean(visibleDocument)} onClose={() => setVisibleDocument(null)} title="Inteiro teor para conferência" size="large">
      {visibleDocument?.warnings?.map((warning, index) => <p className="acervo-notice" key={index}>{researchWarning(warning)}</p>)}
      <div className="legal-research-document" tabIndex={0}>{visibleDocument?.text}</div>
    </Modal>
  </section>
}
